import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { FinanceReconciliationDTO } from '@/api/generated/model';
import { formatMoneyWithCode } from '@/lib/money';
import { cn } from '@/lib/utils';

function num(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : Number(value);
}

export function FinanceReconciliationBanner({
  reconciliation,
  currency,
}: {
  reconciliation: FinanceReconciliationDTO;
  currency: string;
}) {
  const status =
    reconciliation.status ?? (reconciliation.balanced === false ? 'UNBALANCED' : 'BALANCED');
  const residual = num(reconciliation.residual);

  const tone =
    status === 'BALANCED'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
      : status === 'INCOMPLETE_FEES'
        ? 'border-sky-500/40 bg-sky-500/10 text-sky-100'
        : 'border-amber-500/40 bg-amber-500/10 text-amber-100';

  const Icon =
    status === 'BALANCED' ? CheckCircle2 : status === 'INCOMPLETE_FEES' ? Info : AlertTriangle;
  const title =
    status === 'BALANCED'
      ? 'KPI reconciliation balanced'
      : status === 'INCOMPLETE_FEES'
        ? 'Fee columns incomplete — residual is Gross − Creator'
        : 'KPI reconciliation residual is large';

  return (
    <div className={cn('rounded-md border px-4 py-3 text-xs leading-5', tone)} role="status">
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p className="font-medium">{title}</p>
          <p className="text-[11px] opacity-90">
            Gross {formatMoneyWithCode(reconciliation.grossRevenue, currency)} ≈ Platform{' '}
            {formatMoneyWithCode(reconciliation.platformRevenue, currency)} + Creator{' '}
            {formatMoneyWithCode(reconciliation.creatorPayouts, currency)} + Provider fees{' '}
            {formatMoneyWithCode(reconciliation.providerFees, currency)} + Residual{' '}
            {formatMoneyWithCode(residual, currency)}
            {reconciliation.toleranceAbsolute != null &&
              ` (tolerance ${formatMoneyWithCode(reconciliation.toleranceAbsolute, currency)})`}
          </p>
          {(reconciliation.statusReason || reconciliation.formulaNote) && (
            <p className="text-[11px] opacity-80">
              {reconciliation.statusReason ?? reconciliation.formulaNote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
